import PageHeading from '../components/PageHeading';
import { BookOpen, Monitor, ArrowUpRight, ReceiptText, KeyRound, CreditCard, CheckCircle, Clock, Lock, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import React, { useState } from 'react';

const SELF_UPGRADE_STEPS = [
  {
    number: '01',
    icon: <ReceiptText size={18} className="text-brand" aria-hidden="true" />,
    title: '找到卡密和激活地址',
    desc: '付款后，支付成功页面和历史订单里都能看到你的卡密和激活地址。',
    tip: null,
    image: '/images/test/1.jpg',
  },
  {
    number: '02',
    icon: <KeyRound size={18} className="text-brand" aria-hidden="true" />,
    title: '输入卡密完成验证',
    desc: '打开激活地址，把卡密粘贴进去，验证通过后继续下一步。',
    tip: null,
    image: '/images/test/2.jpg',
  },
  {
    number: '03',
    icon: <CreditCard size={18} className="text-brand" aria-hidden="true" />,
    title: '填入充值密钥',
    desc: '获取你账户的充值密钥，粘贴到页面的输入框里，确认账户信息无误。',
    tip: '卡密可以给任何普通号充值，不必和购买邮箱一致。',
    image: '/images/test/3.jpg',
  },
  {
    number: '04',
    icon: <CheckCircle size={18} className="text-brand" aria-hidden="true" />,
    title: '确认信息，点击充值',
    desc: '最后检查一遍卡密和账户信息，没问题就点充值。',
    tip: null,
    image: null,
  },
  {
    number: '05',
    icon: <Clock size={18} className="text-brand" aria-hidden="true" />,
    title: '等待充值到账',
    desc: '提交后稍等一会儿，Plus 开通成功后账号就可以用了。',
    tip: null,
    image: null,
  },
];

const READY_ACCOUNT_STEPS = [
  {
    number: '01',
    icon: <ReceiptText size={18} className="text-brand" aria-hidden="true" />,
    title: '找到账号和卡密',
    desc: '付款后，支付成功页面和历史订单里都能看到一个普通 ChatGPT 账号（含密码）和一张升级卡密。',
    tip: null,
    image: null,
  },
  {
    number: '02',
    icon: <Lock size={18} className="text-brand" aria-hidden="true" />,
    title: '用账号密码登录 ChatGPT',
    desc: '打开 ChatGPT 官网，用收到的账号和密码登录进去。',
    tip: '请勿修改密码或绑定手机，以免影响后续使用。',
    image: null,
  },
  {
    number: '03',
    icon: <Rocket size={18} className="text-brand" aria-hidden="true" />,
    title: '按自助升级流程完成充值',
    desc: '登录后，参考「代充值」标签中的流程，用卡密激活对应订阅。',
    tip: null,
    image: null,
  },
];

const TABS = [
  { key: 'self', label: '代充值' },
  { key: 'ready', label: '成品号' },
  { key: 'shared', label: '网页共享版' },
];

function StepList({ steps }) {
  return (
    <div className="space-y-3">
      {steps.map((step, idx) => (
        <div key={idx}>
          <div className="bg-white rounded-xl border border-line px-4 py-4 flex gap-4 items-start shadow-sm">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-soft text-brand font-bold text-sm flex items-center justify-center">
              {step.number}
            </div>
            <div className="flex-1 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <span className="text-base">{step.icon}</span>
                <span className="text-sm font-semibold text-ink">{step.title}</span>
              </div>
              <p className="text-xs text-muted leading-relaxed">{step.desc}</p>
              {step.tip && (
                <p className="text-xs text-brand bg-brand-soft rounded-lg px-2.5 py-1.5 leading-relaxed">
                  ⚠️ {step.tip}
                </p>
              )}
            </div>
          </div>
          {step.image && (
            <img src={step.image} alt={step.title} className="mt-2 w-full rounded-xl object-cover" />
          )}
        </div>
      ))}
    </div>
  );
}

function SelfUpgradeTab() {
  return (
    <div className="space-y-5">
      <StepList steps={SELF_UPGRADE_STEPS} />

      {/* 充值后没显示 Plus 升级怎么办 */}
      <div className="rounded-2xl border border-brand-line overflow-hidden shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3.5 bg-brand-soft">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">?</span>
          <span className="text-sm font-bold text-ink">充值后没显示 Plus 升级怎么办？</span>
        </div>
        <div className="px-4 pt-3 pb-4 bg-white border-t border-brand-line space-y-3">
          <p className="text-xs text-muted leading-relaxed">
            充值显示成功但 Plus 未到账，<strong className="text-ink">这是正常现象，不是出错了</strong>，请放心。
          </p>
          <div className="bg-canvas border border-line rounded-lg px-3 py-2.5 space-y-1.5">
            <p className="text-xs font-semibold text-ink">为什么会有延迟？</p>
            <p className="text-xs text-muted leading-relaxed">
              OpenAI 在全球部署了多个数据中心。你购买 Plus 时，订单数据写入的是其中一个节点；而你登录 ChatGPT 时，连接的可能是另一个地区的节点。OpenAI 内部各节点之间的数据同步需要一定时间，这就导致部分账号会有短暂延迟——<strong className="text-ink">有的账号秒到，有的需要等一会</strong>，都是正常的。
            </p>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-ink">先做这两步：</p>
            {['刷新 ChatGPT 页面', '退出账号，重新登录'].map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-brand-soft text-brand text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                <span className="text-xs text-ink leading-relaxed">{step}</span>
              </div>
            ))}
          </div>
          <div className="bg-brand-soft rounded-lg px-3 py-2 space-y-1">
            <p className="text-xs text-brand font-semibold">生效时间参考</p>
            <p className="text-xs text-muted leading-relaxed">· <strong className="text-ink">80%</strong> 的用户在 <strong className="text-ink">10 分钟内</strong>生效</p>
            <p className="text-xs text-muted leading-relaxed">· <strong className="text-ink">20%</strong> 的用户延迟到 <strong className="text-ink">30 分钟左右</strong>生效</p>
            <p className="text-xs text-muted leading-relaxed">目前暂无激活成功后延迟超过 1 小时的情况</p>
          </div>
          <div className="border border-brand-line rounded-lg px-3 py-2">
            <p className="text-xs text-ink leading-relaxed">
              如果充值成功后 <strong className="text-brand">超过 2 小时</strong>仍未升级为 Plus，我们提供完善售后保障——<strong className="text-brand">充值不成功，100% 退款</strong>。请联系右下角客服气泡并提供订单号。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReadyAccountTab({ onSwitchToSelf }) {
  return (
    <div className="space-y-5">
      <div className="bg-brand-soft border border-brand-line rounded-xl px-4 py-3">
        <p className="text-xs text-muted leading-relaxed">
          购买成品号后，我们会给你一个<strong className="text-ink">普通 ChatGPT 账号</strong>和一张<strong className="text-ink">升级卡密</strong>。用账号登录后，按卡密走一遍自助升级流程，就能激活对应订阅。
        </p>
      </div>

      <StepList steps={READY_ACCOUNT_STEPS} />

      <button
        onClick={onSwitchToSelf}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-brand hover:bg-brand-hover rounded-xl transition-colors group"
      >
        <div className="flex items-center gap-2">
          <span className="text-base">📖</span>
          <div className="text-left">
            <p className="text-white text-xs font-bold leading-tight">查看自助升级流程教程</p>
            <p className="text-orange-100 text-[10px] leading-tight">一步一步跟着做，几分钟完成升级</p>
          </div>
        </div>
        <span className="text-white font-bold text-sm group-hover:translate-x-0.5 transition-transform">→</span>
      </button>
    </div>
  );
}

function SharedWebTab() {
  return <div className="page-card"><span className="page-icon mb-5"><Monitor /></span><h2 className="text-xl font-bold mb-3">找到网页入口，开始使用</h2><ol className="list-decimal pl-5 space-y-4 text-sm text-muted"><li>支付后，在订单详情中查看专属网页访问入口。</li><li>打开入口，按照订单提供的登录或使用说明操作。</li><li>GPT 单独版使用 GPT；GPT + Claude 组合版按入口提示切换模型。</li></ol><Link to="/orders" className="page-secondary mt-6">查看我的订单<ArrowUpRight size={16} /></Link></div>;
}

function UpgradeGuide() {
  const [activeTab, setActiveTab] = useState('self');

  return (
    <div className="min-h-screen bg-canvas">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">

        <PageHeading Icon={BookOpen} eyebrow="GUIDE · 升级与使用" title="选好产品，跟着步骤用" description="代充值、成品号、网页共享版，按订单交付内容操作。" />

        {/* Tab 切换栏 */}
        <div className="flex bg-white rounded-xl border border-line shadow-sm p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              aria-pressed={activeTab === tab.key}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                activeTab === tab.key
                  ? 'bg-brand text-white shadow-sm'
                  : 'text-muted hover:text-ink'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab 内容 */}
        {activeTab === 'shared' ? <SharedWebTab /> : activeTab === 'self' ? <SelfUpgradeTab /> : <ReadyAccountTab onSwitchToSelf={() => { setActiveTab('self'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />}

      </div>
    </div>
  );
}

export default UpgradeGuide;
